/* client/src/components/Dashboard.tsx */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Alert,
  IconButton,
  useTheme,
  alpha,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  AccountBalanceWallet,
  Inventory,
  TrendingUp,
  AttachMoney,
  Business,
  ShowChart,
  BarChart,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  apiGetTotalInvestment,
  apiGetInventorySummary,
  apiGetTotalInventory,
  apiGetTotalInvestmentAll,
  apiGetDashboard
} from '../services/apiService';
import { formatCurrency, formatNumberWithCommas, formatNumber } from '../utils/formatters';
import { binanceService } from '../services/binanceService';

// Conditionally import recharts only if it's available
let RechartsComponents: any = null;
try {
  RechartsComponents = require('recharts');
} catch (error) {
  console.warn('Recharts not installed. Chart functionality will be limited.');
}

// Interfaces
interface CalculationBreakdown {
  inventory_value: number;
  total_investment: number;
  total_expenses: number;
  provisional_profit: number;
}

interface DashboardData {
  partner: {
    id: number;
    name: string;
    email: string;
  };
  business_units: Array<{
    id: number;
    name: string;
    share_percentage: number;
    profit: number;
    your_share: number;
    capital_balance: number;
    cash_balance: number;
  }>;
  total_profit: number;
  recent_transactions: Array<{
    id: number;
    date: string;
    type: string;
    quantity: number;
    total_amount: number;
    description: string;
    material_name: string;
    business_unit_name: string;
  }>;
  summary: {
    total_transactions: number;
    total_purchases: number;
    total_sales: number;
    total_expenses: number;
    net_profit: number;
    total_capital: number;
    total_inventory_value: number;
    total_investments: number;
    total_invested: number;
    active_investments: number;
    cash_balance: number;
    total_weight?: number;
  };
  metal_rates: {
    goldPerGram: number;
    goldPerOz: number;
    lastUpdated: string;
    source: string;
  };
  inventory_value: number;
  provisional_profit: number;
  calculation_breakdown?: CalculationBreakdown;
}

interface GoldPriceHistory {
  time: number;
  price: number;
}

interface InventorySummaryData {
  unit: string;
  quantity: number;
  value: number;
  average_rate: number;
}

// Helper functions
const formatTime = (timestamp: number) => {
  return new Date(timestamp).toLocaleTimeString();
};

// Function to fetch gold price history for chart using BinanceService
const fetchGoldPriceHistory = async (): Promise<GoldPriceHistory[]> => {
  try {
    const history = await binanceService.getGoldPriceHistory(24);
    
    // Convert USD to AED and per oz to per gram
    const usdToAed = 3.67;
    const ozToGram = 31.1034768;
    
    return history.map(item => ({
      time: item.time,
      price: (item.price * usdToAed) / ozToGram
    }));
  } catch (error) {
    console.error('Error fetching gold price history:', error);
    return [];
  }
};

// Function to get current gold rates using BinanceService
const fetchCurrentGoldRates = async (): Promise<{
  goldPerGram: number;
  goldPerOz: number;
  lastUpdated: string;
  source: string;
} | null> => {
  try {
    const goldPrice = await binanceService.getGoldPrice();
    
    const usdToAed = 3.67;
    const ozToGram = 31.1034768;
    
    const goldPerOz = goldPrice.price * usdToAed;
    const goldPerGram = goldPerOz / ozToGram;
    
    return {
      goldPerGram: parseFloat(goldPerGram.toFixed(2)),
      goldPerOz: parseFloat(goldPerOz.toFixed(2)),
      lastUpdated: new Date().toISOString(),
      source: 'Binance API'
    };
  } catch (error) {
    console.error('Error fetching current gold rates:', error);
    return null;
  }
};

function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [priceHistory, setPriceHistory] = useState<GoldPriceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState<number>(0);
  const [inventorySummary, setInventorySummary] = useState<InventorySummaryData[]>([]);
  const [totalInventoryWeight, setTotalInventoryWeight] = useState<number>(0);
  const [totalInventoryValue, setTotalInventoryValue] = useState<number>(0);
  const [goldRates, setGoldRates] = useState<{
    goldPerGram: number;
    goldPerOz: number;
    lastUpdated: string;
    source: string;
  } | null>(null);
  const theme = useTheme();

  // Function to calculate inventory value using live gold rates
  const calculateInventoryValue = (inventoryData: any, currentGoldRate: number) => {
    if (!inventoryData?.business_units || !currentGoldRate) {
      console.warn('Missing inventory data or gold rate for calculation');
      return { totalValue: 0, totalWeightGrams: 0, summary: [] };
    }

    let totalLiveValue = 0;
    let totalWeightInGrams = 0;
    
    const updatedInventorySummary = inventoryData.business_units.map((unit: any) => {
      const weightInGrams = unit.quantity || 0;
      const liveValue = weightInGrams * currentGoldRate;
      
      totalLiveValue += liveValue;
      totalWeightInGrams += weightInGrams;
      
      return {
        unit: unit.unit || 'Unknown',
        quantity: weightInGrams / 1000, // Convert to kg for display only
        quantity_in_grams: weightInGrams, // Keep original for calculations
        value: liveValue, // Use live calculated value
        average_rate: currentGoldRate, // Use current rate
        stored_value: unit.value || 0 // Keep original for reference
      };
    });
    
    return {
      totalValue: totalLiveValue,
      totalWeightGrams: totalWeightInGrams,
      summary: updatedInventorySummary
    };
  };

  const fetchInventoryData = async (currentGoldRates?: any) => {
  try {
    console.log('🔄 Starting inventory data fetch...');
    const inventoryData = await apiGetTotalInventory();
    console.log('📦 FULL Inventory data received:', inventoryData);
    
    if (inventoryData && inventoryData.business_units) {
      // Use ACTUAL business transaction values (purchase prices) for dashboard table
      const summaryData = inventoryData.business_units.map((unit: any) => ({
        unit: unit.unit || 'Unknown',
        quantity: (unit.quantity || 0) / 1000, // Convert grams to kg for display
        value: unit.value || 0, // ACTUAL purchased value
        average_rate: unit.average_rate || 0 // ACTUAL purchase rate
      }));
      
      const totalWeightKg = summaryData.reduce((total: number, item: any) => 
        total + (item.quantity || 0), 0);
      
      console.log('⚖️ Total inventory weight (actual):', totalWeightKg, 'Kg');
      console.log('💰 Total purchased value (actual):', summaryData.reduce((sum, item) => sum + item.value, 0));
      
      setTotalInventoryWeight(totalWeightKg);
      setInventorySummary(summaryData);
      
      // For live calculations (provisional profit), still use live gold rates
      const goldRatesToUse = currentGoldRates || goldRates;
      const currentGoldRate = goldRatesToUse?.goldPerGram || data?.metal_rates?.goldPerGram || 0;
      
      const liveInventoryValue = totalWeightKg * 1000 * currentGoldRate; // Convert kg to grams
      setTotalInventoryValue(liveInventoryValue);
      
    } else {
      console.error('Invalid inventory data structure:', inventoryData);
      throw new Error('Invalid inventory data structure');
    }
    
  } catch (err) {
    console.error('Error fetching inventory data:', err);
    setTotalInventoryWeight(0);
    setTotalInventoryValue(0);
    setInventorySummary([]);
  }
};

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await apiGetDashboard();
      setData(result);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      console.log('📊 DASHBOARD RAW DATA:', data);
      console.log('💰 Investment breakdown:', data.calculation_breakdown);
      console.log('💵 Summary investment:', data.summary?.total_invested);
      console.log('⚖️ Current inventory value in data:', data.inventory_value);
    }
  }, [data]);

  useEffect(() => {
    const fetchTotalInvestment = async () => {
      try {
        const data = await apiGetTotalInvestmentAll();
        console.log('🔍 INVESTMENT API RAW RESPONSE:', data);
        setTotalInvestment(data.total_invested || data.total_investment || 0);
      } catch (err) {
        console.error('Error fetching total investment:', err);
        if (data?.summary?.total_invested) {
          setTotalInvestment(data.summary.total_invested);
        }
      }
    };

    fetchTotalInvestment();
  }, [data]);

  // Recalculate inventory when gold rates change
  useEffect(() => {
    if (goldRates && inventorySummary.length > 0) {
      console.log('🔄 Gold rates changed, recalculating inventory...');
      fetchInventoryData();
    }
  }, [goldRates]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Fetch gold rates FIRST
        console.log('💰 Fetching gold rates...');
        const goldRatesData = await fetchCurrentGoldRates();
        setGoldRates(goldRatesData);
        console.log('✅ Gold rates fetched:', goldRatesData?.goldPerGram);
        
        // Then fetch dashboard data
        console.log('📊 Fetching dashboard data...');
        const dashboardData = await apiGetDashboard();
        
        // Merge the gold rates into dashboard data
        const mergedData = {
          ...dashboardData,
          metal_rates: goldRatesData || dashboardData.metal_rates
        };
        
        setData(mergedData);
        
        // Then fetch inventory data (passing current gold rates)
        console.log('📦 Fetching inventory data...');
        await fetchInventoryData(goldRatesData);
        
        // Fetch price history for chart
        console.log('📈 Fetching price history...');
        const history = await fetchGoldPriceHistory();
        setPriceHistory(history);
        
        console.log('✅ All data fetched successfully');
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [retryCount]);

  const refreshAllData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Refreshing all data...');
      
      // Refresh all data in parallel but ensure proper order for calculations
      const [goldRatesData, history, dashboardData] = await Promise.all([
        fetchCurrentGoldRates(),
        fetchGoldPriceHistory(),
        apiGetDashboard()
      ]);
      
      // Set gold rates first
      setGoldRates(goldRatesData);
      
      // Merge data
      const mergedData = {
        ...dashboardData,
        metal_rates: goldRatesData || dashboardData.metal_rates
      };
      
      setData(mergedData);
      setPriceHistory(history);
      
      // Then refresh inventory data with new gold rates
      await fetchInventoryData(goldRatesData);
      
      console.log('✅ Data refresh completed');
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color,
    subtitle,
    tooltip 
  }: { 
    title: string; 
    value: string; 
    icon: React.ReactElement; 
    color: string;
    subtitle?: string;
    tooltip?: string;
  }) => (
    <Card sx={{ 
      height: '100%', 
      transition: 'transform 0.2s', 
      '&:hover': { transform: 'translateY(-4px)' },
      position: 'relative'
    }}>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton 
            size="small" 
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h5" component="div">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
            </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(color, 0.1),
              color: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 2
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Add this component for the calculation breakdown (UPDATED VERSION)
  const CalculationBreakdownCard = ({ 
    data, 
    actualInvestment, 
    liveInventoryValue 
  }: { 
    data: CalculationBreakdown; 
    actualInvestment: number;
    liveInventoryValue: number;
  }) => {
    // Use the LIVE inventory value instead of the backend value
    const effectiveInventoryValue = liveInventoryValue > 0 ? liveInventoryValue : data.inventory_value;
    const effectiveInvestment = data.total_investment > 0 ? data.total_investment : actualInvestment;
    const effectiveProfit = effectiveInventoryValue - effectiveInvestment - data.total_expenses;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Provisional Profit Calculation Breakdown
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (Using live gold rates)
            </Typography>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Total Inventory Value:
              </Typography>
              <Typography variant="h6">
                د.{formatCurrency(effectiveInventoryValue)}
              </Typography>
              {liveInventoryValue > 0 && liveInventoryValue !== data.inventory_value && (
                <Typography variant="caption" color="info.main">
                  Live calculation
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Total Investment:
              </Typography>
              <Typography variant="h6">
                د.{formatCurrency(effectiveInvestment)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Total Expenses:
              </Typography>
              <Typography variant="h6">
                د.{formatCurrency(data.total_expenses)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Provisional Profit:
              </Typography>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                د.{formatCurrency(effectiveProfit)}
              </Typography>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Formula: Inventory Value ({formatCurrency(effectiveInventoryValue)}) - Total Investment ({formatCurrency(effectiveInvestment)}) - Total Expenses ({formatCurrency(data.total_expenses)}) = {formatCurrency(effectiveProfit)}
          </Typography>
          {liveInventoryValue > 0 && liveInventoryValue !== data.inventory_value && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              Note: Using live inventory value based on current gold rate instead of stored value
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const GoldRateChart = () => {
    if (!RechartsComponents) {
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              24h Gold Price Trend
            </Typography>
            <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Chart functionality requires recharts library
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => window.open('https://www.npmjs.com/package/recharts', '_blank')}
              >
                Install Recharts
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Run: npm install recharts
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line } = RechartsComponents;
    
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            24h Gold Price Trend
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={priceHistory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tickFormatter={formatTime}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={['dataMin - 1', 'dataMax + 1']}
                tickFormatter={(value: number) => `د.${value.toFixed(2)}`}
              />
              <Tooltip 
                formatter={(value: number) => [`د.${value.toFixed(2)}`, 'Price']}
                labelFormatter={(label: number) => `Time: ${formatTime(label)}`}
              />
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke="#F59E0B" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const InventorySummary = () => {
  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Inventory Summary (Actual Business Values)
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (inventorySummary.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Inventory Summary (Actual Business Values)
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography color="text.secondary">No inventory data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Use ACTUAL business transaction values from inventory summary
  const totalWeight = inventorySummary.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalValue = inventorySummary.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  // Calculate average rate based on ACTUAL purchase values
  let totalAverageRate = 0;
  if (totalWeight > 0 && totalValue > 0) {
    // Convert totalWeight from kg to grams for rate calculation
    const totalWeightGrams = totalWeight * 1000;
    totalAverageRate = totalValue / totalWeightGrams;
  }
  
  // Ensure it's a valid number
  totalAverageRate = Number.isNaN(totalAverageRate) ? 0 : totalAverageRate;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Inventory Summary (Actual Business Values)
        </Typography>
        
        {/* Display current gold rate for reference */}
        {goldRates?.goldPerGram && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current gold rate: {formatCurrency(goldRates.goldPerGram)}/g (for reference)
          </Typography>
        )}
        
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }} align="right">Quantity (Kg)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }} align="right">Purchased Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }} align="right">Avg Purchase Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventorySummary.map((item) => (
                <TableRow key={item.unit}>
                  <TableCell sx={{ fontWeight: 'medium' }}>{item.unit}</TableCell>
                  <TableCell align="right">{(Number(item.quantity) || 0).toFixed(3)}</TableCell>
                  <TableCell align="right">{formatCurrency(Number(item.value) || 0)}</TableCell>
                  <TableCell align="right">
                    {typeof item.average_rate === 'number' ? 
                     formatCurrency(item.average_rate) : 
                     formatCurrency(Number(item.average_rate) || 0)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Total Row */}
              <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">{totalWeight.toFixed(3)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">{formatCurrency(totalValue)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  {formatCurrency(totalAverageRate)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Calculation breakdown */}
        <Box sx={{ mt: 2, p: 1, backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Calculation:</strong> Total weight: {(totalWeight * 1000).toLocaleString()} grams × {formatCurrency(totalAverageRate)}/g = {formatCurrency(totalValue)}
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing actual purchased values from business transactions
        </Typography>
      </CardContent>
    </Card>
  );
};

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          Unable to load dashboard data. Please try again.
        </Typography>
      </Box>
    );
  }
 
  // Calculate effective provisional profit - FIXED
  const effectiveInventoryValue = totalInventoryValue > 0 ? totalInventoryValue : (data?.inventory_value || 0);
  
  // Use the same calculation logic as the breakdown component
  const effectiveProvisionalProfit = data?.calculation_breakdown ? 
    (effectiveInventoryValue - 
     (data.calculation_breakdown.total_investment > 0 ? data.calculation_breakdown.total_investment : (totalInvestment || data?.summary?.total_invested || 0)) - 
     (data.calculation_breakdown.total_expenses || data?.summary?.total_expenses || 0)) : 
    (effectiveInventoryValue - (totalInvestment || data?.summary?.total_invested || 0) - (data?.summary?.total_expenses || 0));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={refreshAllData}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="info" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}
      
      {/* Add calculation breakdown if available - UPDATED WITH liveInventoryValue PROP */}
      {data?.calculation_breakdown && (
        <CalculationBreakdownCard 
          data={data.calculation_breakdown} 
          actualInvestment={totalInvestment || data?.summary?.total_invested || 0}
          liveInventoryValue={effectiveInventoryValue} // This is the key fix
        />
      )}
      
      <Grid container spacing={3}>
        {/* Live Gold Rate */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" color="primary">
                  Live Gold Rate
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={refreshAllData}
                  title="Refresh data"
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
              
              {loading ? (
                <LinearProgress sx={{ my: 2 }} />
              ) : (
                <>
                  <Typography variant="h4" sx={{ my: 2 }}>
                    {formatCurrency(goldRates?.goldPerGram || data?.metal_rates?.goldPerGram || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    per gram
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(goldRates?.goldPerOz || data?.metal_rates?.goldPerOz || 0)} per oz
                  </Typography>
                  {(goldRates?.source || data?.metal_rates?.source) && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Source: {goldRates?.source || data?.metal_rates?.source}
                    </Typography>
                  )}
                  {(goldRates?.lastUpdated || data?.metal_rates?.lastUpdated) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Updated: {new Date(goldRates?.lastUpdated || data?.metal_rates?.lastUpdated || '').toLocaleTimeString()}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cash Balance */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Cash Balance"
            value={formatCurrency(data?.summary?.cash_balance || 0)}
            icon={<AccountBalanceWallet />}
            color={theme.palette.success.main}
            tooltip="Total cash balance across all accessible business units"
          />
        </Grid>

        {/* Total Investment */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total Investment"
            value={formatCurrency(totalInvestment || data?.summary?.total_invested || 0)}
            icon={<AttachMoney />}
            color={theme.palette.primary.main}
            subtitle={`${data?.summary?.total_investments || 0} investments`}
            tooltip="Total investments across all accessible business units"
          />
        </Grid>

        {/* Inventory Value */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Inventory Value"
            value={formatCurrency(effectiveInventoryValue)}
            icon={<Inventory />}
            color={theme.palette.info.main}
            subtitle="Live gold price adjusted"
            tooltip={`Total inventory value calculated using current gold rates (${totalInventoryWeight.toFixed(2)} kg × د.${(goldRates?.goldPerGram || 0).toFixed(2)}/g)`}
          />
        </Grid>

        {/* Provisional Profit */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Provisional Profit"
            value={formatCurrency(effectiveProvisionalProfit || 0)}
            icon={<ShowChart />}
            color={theme.palette.success.main}
            subtitle="Based on current gold rate"
            tooltip={`Calculated as: Inventory Value (${formatCurrency(effectiveInventoryValue)}) - Total Investment (${formatCurrency(totalInvestment || data?.summary?.total_invested || 0)}) - Total Expenses (${formatCurrency(data?.summary?.total_expenses || 0)})`}
          />
        </Grid>

        {/* Total Expenses */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total Expenses"
            value={formatCurrency(data?.summary?.total_expenses || 0)}
            icon={<TrendingUp />}
            color={theme.palette.warning.main}
            tooltip="Total expenses across all business units"
          />
        </Grid>

        {/* Provisional Profit Margin */}
        <Grid item xs={12} md={6} lg={3}>
          {effectiveInventoryValue && effectiveInventoryValue > 0 ? (
            <StatCard
              title="Provisional Profit Margin"
              value={`${((effectiveProvisionalProfit || 0) / effectiveInventoryValue * 100).toFixed(1)}%`}
              icon={<BarChart />}
              color={theme.palette.secondary.main}
              subtitle={`Profit: ${formatCurrency(effectiveProvisionalProfit || 0)}`}
              tooltip="Provisional Profit as percentage of Inventory Value"
            />
          ) : (
            <StatCard
              title="Provisional Profit Margin"
              value="0.0%"
              icon={<BarChart />}
              color={theme.palette.secondary.main}
              subtitle="No inventory"
            />
          )}
        </Grid>

        {/* Total Weight */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total inventory"
            value={`${totalInventoryWeight.toFixed(2)} Kg`}
            icon={<Inventory />}
            color={theme.palette.info.main}
            subtitle="Total inventory weight"
            tooltip="Total weight of all inventory items in stock"
          />
        </Grid>

        {/* Gold Price Chart and Inventory Summary */}
        <Grid item xs={12} lg={6}>
          <GoldRateChart />
        </Grid>
        <Grid item xs={12} lg={6}>
          <InventorySummary />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;